import { useEffect, useRef, useState } from 'react'
import { confirmLinkWithCode, confirmLinkWithPassword, linkMethodURL, sendLinkCode } from '../shared/sso-workspace/link-confirm-api'
import { COPY, REASON, lastNotice, nextSendFrom, reasonOf, secondsUntil, serverSentence, triesSpentForSignIn } from './open-link-confirm-copy'

// The held sign-in's confirm page's state and steps (open-link-confirm.js
// draws it; link-confirm-screens.js are its screens). One place decides
// which ways are left, what each refusal says and what it disables; the
// screens only draw that.
//
// token: the held sign-in's; state: GET link-confirmation's held answer.
// onConfirmed(next), onRecheck(), onSpent(error) -> bool, onOtherBrowser():
// open-link.js's, for the answers that leave this page.

// Re-renders every second while active (a countdown, a code's life).
const useTick = active => {
  const [, setTick] = useState(0)
  useEffect(() => {
    if (!active) return undefined
    const id = setInterval(() => setTick(t => t + 1), 1000)
    return () => clearInterval(id)
  }, [active])
}

export const useLinkConfirm = ({ token, state, onConfirmed, onRecheck, onSpent, onOtherBrowser }) => {
  const methods = state.methods || {}

  const [screen, setScreen] = useState('ways') // ways (A) | code (B)
  const [ways, setWays] = useState({ password: Boolean(methods.password), email: Boolean(methods.emailCode), linked: Array.isArray(methods.linked) ? methods.linked : [] })
  const [code, setCode] = useState(state.code || null) // the last code sent: { expiresAt, resendAfter }
  const [nextSend, setNextSend] = useState(() => (state.code ? nextSendFrom(state.code.resendAfter) : undefined))
  const [locked, setLocked] = useState({ password: false, code: false })
  const [invalid, setInvalid] = useState({ password: false, code: false })
  const [busy, setBusy] = useState(null) // password | send | verify | a method's identifier
  const [password, setPasswordValue] = useState('')
  const [typed, setTypedValue] = useState('')
  const [sent, setSent] = useState(null)
  const [failure, setFailure] = useState(null) // what took a way away, for the no-way card's reference
  const [ssoOnly, setSsoOnly] = useState(false) // the password went because the domain requires single sign-on
  const passwordInput = useRef(null)
  const codeInput = useRef(null)

  const codeOutstanding = Boolean(code && secondsUntil(code.expiresAt) > 0)
  const codeExpired = Boolean(code) && !codeOutstanding
  const canSend = ways.email && nextSend !== null
  const sendWait = canSend ? secondsUntil(nextSend) : 0

  // the countdown, and a code's life (the page says when it has expired)
  useTick(sendWait > 0 || codeOutstanding)

  // Whether the page offers a way to confirm other than `except`
  // ('password', 'email' or a method's identifier).
  const another = except =>
    (except !== 'password' && ways.password && !locked.password) ||
    (except !== 'email' && (canSend || codeOutstanding)) ||
    ways.linked.some(m => m.identifier !== except)

  // The last method proof's outcome, as said on arrival.
  const [arrival] = useState(() => {
    const last = state.last
    const id = last && last.provider ? last.provider.identifier : undefined
    return lastNotice(last, { offered: Boolean(id) && ways.linked.some(m => m.identifier === id), another: another(id) })
  })

  const [notice, setNotice] = useState(arrival)

  // Back from a method's sign-in through the browser's back/forward cache:
  // the hand-off is over.
  useEffect(() => {
    const onShow = e => { if (e.persisted) setBusy(null) }
    window.addEventListener('pageshow', onShow)
    return () => window.removeEventListener('pageshow', onShow)
  }, [])

  const setPassword = value => {
    setPasswordValue(value)
    setInvalid(i => ({ ...i, password: false }))
  }

  const setTyped = value => {
    setTypedValue(value)
    setInvalid(i => ({ ...i, code: false }))
  }

  // What every step answers alike: spent, another browser, no longer held,
  // changed meanwhile. True when handled.
  const common = error => {
    if (onSpent(error)) return true
    const reason = reasonOf(error)
    if (reason === REASON.otherBrowser) { onOtherBrowser(); return true }
    if (reason === REASON.notRequired) { onRecheck(); return true }
    if (reason === REASON.changed) { setNotice({ tone: 'warn', text: COPY.changed }); return true }
    return false
  }

  const confirmPassword = async e => {
    e.preventDefault()
    if (!password || busy) return
    setBusy('password')
    setNotice(null)
    try {
      const out = await confirmLinkWithPassword(token, password)
      onConfirmed(out && out.next)
      return
    } catch (error) {
      if (common(error)) return
      switch (reasonOf(error)) {
        case REASON.failed:
          setPasswordValue('')
          setInvalid(i => ({ ...i, password: true }))
          setNotice({ tone: 'bad', text: COPY.wrongPassword(another('password')) })
          if (passwordInput.current) passwordInput.current.focus()
          break
        case REASON.locked:
          setLocked(l => ({ ...l, password: true }))
          setNotice({ tone: 'bad', text: COPY.locked(serverSentence(error), another('password')) })
          break
        case REASON.rateLimited:
          // only this sign-in's own tries are final (review L3)
          if (triesSpentForSignIn(error)) setLocked(l => ({ ...l, password: true }))
          setNotice({ tone: 'warn', restart: true, text: COPY.tooManyTries(serverSentence(error)) })
          break
        case REASON.unavailable:
          setWays(w => ({ ...w, password: false }))
          setSsoOnly(true)
          setFailure(error)
          setNotice({ tone: 'warn', text: COPY.ssoOnly(another('password')) })
          break
        default:
          setNotice({ tone: 'bad', text: COPY.passwordFault, failure: error })
      }
    } finally {
      setBusy(null)
    }
  }

  const send = async () => {
    if (busy) return
    const resend = screen === 'code'
    setBusy('send')
    setNotice(null)
    setSent(null)
    try {
      const out = (await sendLinkCode(token)) || {}
      setCode({ expiresAt: out.expiresAt, resendAfter: out.resendAfter })
      setNextSend(nextSendFrom(out.resendAfter))
      setTypedValue('')
      setInvalid(i => ({ ...i, code: false }))
      setScreen('code')
      if (resend) setSent(COPY.resent)
    } catch (error) {
      if (common(error)) return
      switch (reasonOf(error)) {
        case REASON.codeRateLimited: {
          const after = nextSendFrom(error.data.resendAfter)
          setNextSend(after)
          if (after === null) {
            setNotice(resend
              ? { tone: 'warn', key: 'no_more_sends', text: COPY.noMoreSendsCode }
              : { tone: 'warn', restart: true, text: COPY.noMoreSends(another('email')) })
          } else {
            setNotice({ tone: 'warn', text: COPY.sendLimit(another('email')) })
          }
          break
        }
        case REASON.codeUnavailable:
          setWays(w => ({ ...w, email: false }))
          setFailure(error)
          setNotice({ tone: 'warn', text: resend ? COPY.resendUnavailable(another('email')) : COPY.emailUnavailable(another('email')) })
          break
        default:
          setNotice({ tone: 'bad', text: COPY.sendFault, failure: error })
      }
    } finally {
      setBusy(null)
    }
  }

  const verify = async e => {
    e.preventDefault()
    if (busy) return
    setSent(null)
    if (!/^\d{8}$/.test(typed.replace(/\s+/g, ''))) {
      setInvalid(i => ({ ...i, code: true }))
      setNotice({ tone: 'bad', text: COPY.codeShape })
      return
    }
    // The code on the page has expired (review M1): auth would answer the
    // same 400 as a wrong code, so say what is true and what is left.
    if (codeExpired) {
      const next = canSend ? 'send' : another('email') ? 'another' : 'restart'
      setNotice({ tone: 'warn', text: COPY.codeExpired(next), restart: next === 'restart' })
      return
    }
    setBusy('verify')
    setNotice(null)
    try {
      const out = await confirmLinkWithCode(token, typed.trim())
      onConfirmed(out && out.next)
      return
    } catch (error) {
      if (common(error)) return
      switch (reasonOf(error)) {
        case REASON.failed:
          setTypedValue('')
          setInvalid(i => ({ ...i, code: true }))
          setNotice({ tone: 'bad', text: COPY.wrongCode(canSend && !locked.code, another('email')) })
          if (codeInput.current) codeInput.current.focus()
          break
        case REASON.rateLimited:
          if (triesSpentForSignIn(error)) setLocked(l => ({ ...l, code: true }))
          setNotice({ tone: 'warn', restart: true, text: COPY.tooManyTries(serverSentence(error)) })
          break
        default:
          setNotice({ tone: 'bad', text: COPY.codeFault, failure: error })
      }
    } finally {
      setBusy(null)
    }
  }

  // A method the account already has: a full-page navigation to auth's
  // start, which goes to that method's sign-in (or back here).
  const startMethod = method => {
    if (busy) return
    setBusy(method.identifier)
    window.location.assign(linkMethodURL(token, method.identifier))
  }

  const toWays = () => {
    setNotice(null)
    setSent(null)
    setScreen('ways')
  }

  const toCode = () => {
    setNotice(null)
    setScreen('code')
  }

  // Nothing left to confirm with on this page.
  const noWay = !ways.password && !canSend && !codeOutstanding && ways.linked.length === 0

  // What the no-way card says: why, and what happened last.
  const noWayBody = ssoOnly ? COPY.noWaySso : state.last || nextSend === null || code ? COPY.noWayLeft : COPY.noWay
  const noWayLead = codeExpired ? { tone: 'warn', text: COPY.codeExpiredLead } : arrival ? { tone: arrival.tone, text: arrival.base } : null

  return {
    screen, ways, code, nextSend, locked, invalid, busy, password, typed, sent, notice, failure,
    codeOutstanding, codeExpired, canSend, sendWait, noWay, noWayBody, noWayLead,
    passwordInput, codeInput,
    another, setPassword, setTyped, confirmPassword, send, verify, startMethod, toWays, toCode
  }
}
